/* eslint-disable spellcheck/spell-checker */
import { getDateTimeFromIso, getIsoStringFromDateTime } from './helpers';
import { APSV2Form, CreateFormV2RequestBody, FormStatuses } from '../../types';

/* 
Translate Cacahute API query params to satisfy APS query params
method name: getFieldReports
Cacahute API: /v5/project/${projectId}/field-report-types/${formTemplateId}/reports/${formId}
APS API: /development/v2/projects/${projectId}/form-templates/${formTemplateId}/forms
*/
const translateBody = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  body: Record<string, any>,
): CreateFormV2RequestBody => {
  // remove unused params
  delete body['pg_form'];

  const apsParams: CreateFormV2RequestBody = {
    ...body,
  };

  // simple key mapping
  const keyMapping: Record<string, keyof CreateFormV2RequestBody> = {
    report_date: 'formDate',
    location_id: 'locationId',
    description: 'notes',
    assignee_id: 'assigneeId',
    assignee_type: 'assigneeType',
    title: 'description',
  };

  type ValueMappingRecord = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [K in keyof CreateFormV2RequestBody]: (oldValue: any) => unknown;
  };
  // An object of value translation functions: key is the new query param key, the value is the translation function
  const valueMapping: ValueMappingRecord = {
    formDate: (date: string) => {
      return getIsoStringFromDateTime(date).substring(0, 10);
    },
  };

  // translate keys/values
  Object.entries(apsParams).forEach(([key, value]) => {
    const newKey = keyMapping[key] || key;
    // remove old params from cacahute, type conversion is just here for typescript
    delete apsParams[key as unknown as keyof CreateFormV2RequestBody];

    const newValue = valueMapping[newKey]
      ? valueMapping[newKey]?.(value)
      : value;

    if (newValue !== null) {
      apsParams[newKey] = newValue;
    }
  });

  return apsParams;
};

// translate APS response to Cacahute, this should be only temporary until we fully migrate to APS
const translateResponse = (
  projectId: string,
  form: APSV2Form,
  forgeUserMappingByOxygenID: Record<string, string>,
) => {
  const mapResponseData = (form: APSV2Form) => {
    type FormKeyMappingRecord = {
      [K in keyof APSV2Form]: string;
    };
    const formKeyMapping: FormKeyMappingRecord = {
      createdBy: 'created_by',
      createdAt: 'created_at',
      formDate: 'report_date',
      formTemplateId: 'type',
      id: 'uid',
      lastFetchedAt: 'last_fetched_at',
      updatedAt: 'updated_at',
      updatedBy: 'updated_by',
      userCreatedAt: 'user_created_at',
      nativeForm: 'pg_form',
      pdfFile: 'file',
      formNum: 'report_num',
      dueDate: 'due_date',
      locationId: 'location_id',
      assigneeId: 'assignee_id',
      assigneeType: 'assignee_type',
      description: 'title',
      notes: 'description',
      lastSubmittedAt: 'last_submitted_at',
      lastSubmittedBy: 'last_submitted_by',
      lastReopenedBy: 'last_reopened_by',
      weatherId: 'weather_id',
    };

    // An object of value translation functions: key is the new query param key, the value is the translation function
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const valueMapping: Record<string, (apsValue: any) => any> = {
      status: (oldValue: FormStatuses) => {
        const statusMap: Record<FormStatuses, string> = {
          inProgress: 'draft',
          inReview: 'in_review',
          closed: 'submitted',
          archived: 'archived',
        };
        return statusMap[oldValue] || oldValue;
      },
      due_date: getDateTimeFromIso,
      created_at: getDateTimeFromIso,
      last_fetched_at: getDateTimeFromIso,
      report_date: getDateTimeFromIso,
      user_created_at: getDateTimeFromIso,
      updated_at: getDateTimeFromIso,
      assignee_id: (oldValue: string) => {
        return forgeUserMappingByOxygenID[oldValue];
      },
      created_by: (oldValue: string) => {
        return forgeUserMappingByOxygenID[oldValue];
      },
      updated_by: (oldValue: string) => {
        return forgeUserMappingByOxygenID[oldValue];
      },
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cacaForm: Record<string, any> = {};
    // translate keys/values
    Object.entries(form).forEach(([key, value]) => {
      const newKey =
        formKeyMapping[key as unknown as keyof FormKeyMappingRecord] || key;
      const newValue = valueMapping[newKey]
        ? valueMapping[newKey]?.(value)
        : value;

      if (newValue !== null) {
        cacaForm[newKey] = newValue;
      }
    });

    cacaForm.project = projectId;
    if (form.nativeForm) {
      cacaForm.form_uid = form.nativeForm.id;
      cacaForm.pg_form = {};
    } else if (form.pdfFile) {
      const urls = {
        file: form.pdfFile.pdfUrl,
        thumbnail: form.pdfFile.pdfThumbnail,
      };
      cacaForm.file = {
        filename: form.pdfFile.fileName,
        uid: form.pdfFile.id,
        size: form.pdfFile.size,
        urls,
      };

      cacaForm.urls = urls;
    }

    return cacaForm;
  };

  return mapResponseData(form);
};

const createFormTransformers = {
  translateBody,
  translateResponse,
};

export default createFormTransformers;
